import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApiError, describeError } from "../../api/client";
import type {
  AdminCampaignDetail,
  AdminCampaignMonitoring,
  AdminOverview,
  AdminTelemetryBreakdown,
  AdminTelemetryDailySeries,
  AdminTelemetryGroupBy,
  CampaignStateCount,
  CampaignTelemetry,
  OperationalMetric,
} from "../../api/types";
import { getCampaign, getCampaignMonitoring } from "../campaigns/api";
import { EmptyState } from "../../components/EmptyState";
import { Pagination } from "../../components/Pagination";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { DeliveryProgressBar } from "../../components/DeliveryProgressBar";
import { SegmentedControl } from "../../components/SegmentedControl";
import { formatCount } from "../../formatting/number";
import { isTerminalLifecycleState } from "../../formatting/status";
import { useQuery, type UseQueryResult } from "../../state/useQuery";
import { getOverview } from "../overview/api";
import { CampaignSelector } from "./CampaignSelector";
import { getCampaignTelemetry, getTelemetryBreakdown, getTelemetryDaily } from "./api";
import {
  buildCampaignStateDistribution,
  buildDailyChartData,
  buildUnmeasuredMetricsNote,
  formatBreakdownMeta,
  formatDeliveryRateFromMetrics,
  formatMetricDisplay,
  groupLabelFor,
  metricValue,
  subtitleFor,
  type TelemetryPeriod,
} from "./derived";

/**
 * `/telemetria` — REDISENO-PANEL.md §6. Replaces the previous
 * DataTable/evidence-matrix screen entirely: KPI cards (account-wide or
 * per-campaign), a 30-day account-wide bar chart, a breakdown card
 * (plantilla/show), and a campaign-states card. `TelemetryEvidenceMatrix`
 * (the old evidence table) is left in place, unused, per this codebase's own
 * "stop using it, never delete it" precedent (T4/T5) — nothing else imports
 * it any more.
 */
export function TelemetryScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignId = searchParams.get("campaignId") ?? "";
  const hasCampaign = Boolean(campaignId);

  const [period, setPeriod] = useState<TelemetryPeriod>("metrics");
  const [groupBy, setGroupBy] = useState<AdminTelemetryGroupBy>("template");

  const handleSelectCampaign = useCallback(
    (id: string) => {
      const next = new URLSearchParams(searchParams);
      if (id) {
        next.set("campaignId", id);
      } else {
        next.delete("campaignId");
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const detailQuery = useQuery<AdminCampaignDetail>({
    key: ["campaign", campaignId],
    queryFn: () => getCampaign(campaignId),
    enabled: hasCampaign,
  });

  // Codex finding (PR #385 review round 3): when `?campaignId=` names a
  // nonexistent or foreign campaign, `monitoringQuery`/`telemetryQuery`
  // below used to stay enabled solely because `campaignId` was nonempty —
  // their 404 responses never produce terminal data, so the polling loop
  // kept rescheduling both requests indefinitely (eventually every 80s via
  // backoff) even though the detail branch already renders "Campaña no
  // encontrada". Only enable them once a matching campaign detail has
  // actually resolved.
  const detailResolvedForCurrentCampaign = hasCampaign && detailQuery.data?.campaignId === campaignId;

  // Codex finding 7 (PR #385): same `campaign-monitoring` role as
  // `CampaignDetailScreen` -- a lightweight, ALREADY-POLLED source for
  // `estado` that the telemetry poll's terminal check below keys off,
  // instead of the unpolled `detailQuery` (same Codex finding 5 / PR #384
  // fix: `detailQuery.data.estado` can lag a background dispatch consumer's
  // real transition to terminal).
  //
  // Codex P2 (PR #385): `useQuery` keeps the PREVIOUSLY selected campaign's
  // monitoring object in `data` while the newly-selected campaign's own
  // request is still in flight or fails (state/useQuery.ts never resets
  // `data` on a key change). Without checking that the retained object's own
  // `campaignId` still matches the CURRENTLY selected `campaignId`, a
  // terminal `estado` left over from the OLD (terminal) campaign would
  // permanently stop both this poll and the telemetry poll below for the
  // newly selected, still-non-terminal campaign.
  const monitoringQuery = useQuery<AdminCampaignMonitoring>({
    key: ["campaign-monitoring", campaignId],
    queryFn: () => getCampaignMonitoring(campaignId),
    enabled: detailResolvedForCurrentCampaign,
    pollIntervalMs: 10_000,
    isTerminal: (monitoring) =>
      monitoring && monitoring.campaignId === campaignId ? isTerminalLifecycleState(monitoring.estado) : false,
  });

  // Codex finding (PR #385 review round 3): this poll used to stop the
  // moment `monitoringQuery`'s estado reached a terminal lifecycle state.
  // `CampaignDispatchService.cs:436-438` marks a campaign "completada"
  // immediately after its own enqueue loop finishes, while
  // `EmailDeliveryWebhookFunction.cs:115-125` records delivered/bounced
  // events ASYNCHRONOUSLY afterward — stopping on dispatch completion
  // permanently froze the delivered/error KPIs before those late webhook
  // events could ever arrive. Delivery telemetry stays refreshable
  // independently of the campaign's own lifecycle state (never auto-stops);
  // `monitoringQuery`'s own poll above is unaffected and still stops once
  // ITS data is terminal, since encolados/suprimidos/fallidos genuinely do
  // stop changing at that point.
  const telemetryQuery = useQuery<CampaignTelemetry>({
    key: ["campaign-telemetry", campaignId],
    queryFn: () => getCampaignTelemetry(campaignId),
    enabled: detailResolvedForCurrentCampaign,
    pollIntervalMs: 10_000,
    isTerminal: () => false,
  });

  // Codex finding (PR #385 review round 3): true only when `telemetryQuery`
  // holds a genuinely RETAINED data object for a different campaign (the
  // "useQuery never resets data on a key change" quirk) — distinct from
  // `data === undefined`, which is the current campaign's own normal
  // loading/failed-with-nothing-cached state and must keep its real
  // loading/error presentation untouched.
  const telemetryStaleForCurrentCampaign = telemetryQuery.data !== undefined && telemetryQuery.data.campaignId !== campaignId;

  // Shared with OverviewScreen (same cache key) — drives the account-wide
  // KPI cards, Contactos suprimidos, and Campañas por estado.
  const overviewQuery = useQuery<AdminOverview>({
    key: ["overview"],
    queryFn: getOverview,
  });

  // §9.5 ⚠: always account-wide, even with a campaign selected — the
  // endpoint has no campaign filter; the section title says so explicitly.
  const dailyQuery = useQuery<AdminTelemetryDailySeries>({
    key: ["telemetry-daily", 30],
    queryFn: () => getTelemetryDaily(30),
  });

  const breakdownQuery = useQuery<AdminTelemetryBreakdown>({
    key: ["telemetry-breakdown", groupBy],
    queryFn: () => getTelemetryBreakdown(groupBy),
  });

  // Codex finding (PR #385 review round 3): `monitoringQuery` receives a
  // campaign's transition to a terminal state through its own 10s poll, but
  // `detailQuery` is only fetched when the campaign key changes. Reading
  // `estado` exclusively from the unpolled detail left this subtitle saying
  // "Enviando" even after monitoring had observed "Completada"/"Fallida"
  // (and stopped polling). Use the matched monitoring state when it is for
  // the CURRENT campaign (same guard `isTerminal` above already applies —
  // `useQuery` can retain a previous campaign's data across a key change),
  // falling back to the detail's own estado otherwise.
  const monitoringEstadoForCurrentCampaign =
    monitoringQuery.data && monitoringQuery.data.campaignId === campaignId ? monitoringQuery.data.estado : undefined;
  // Codex review round 4 (P2, PR #385): a truthy-only `detailQuery.data`
  // check kept a PREVIOUSLY-selected campaign's retained detail object
  // (same "useQuery never resets data on a key change" quirk already
  // guarded for `detailResolvedForCurrentCampaign` above) naming that OLD
  // campaign here — and, once the newly-selected campaign's own detail
  // request failed, that same stale truthy object suppressed the
  // not-found/error branch below entirely instead of surfacing it. Reuses
  // the exact same `campaignId` match as `detailResolvedForCurrentCampaign`.
  const campaignForSubtitle =
    detailResolvedForCurrentCampaign && detailQuery.data
      ? {
          nombre: detailQuery.data.nombre,
          estado: monitoringEstadoForCurrentCampaign ?? detailQuery.data.estado,
          fechaProgramada: detailQuery.data.fechaProgramada,
        }
      : null;
  // Advisory R3-subtitle-stuck-loading: a failed detail fetch (404, 500, …)
  // must NOT keep claiming "Cargando…" forever just because `detailQuery.data`
  // is still absent — only an actually-pending (no error yet) fetch should.
  //
  // Codex finding (PR #385 review round 3): once a campaign deep link's
  // first detail request returns 404/500, `campaignForSubtitle` is null and
  // `subtitleFor` interprets null as account-wide — the header said "Toda
  // la cuenta · ..." while the URL/selector still identify a campaign and
  // the body reports it as unavailable. A neutral campaign-error subtitle
  // instead of switching the displayed scope.
  const subtitle =
    hasCampaign && !detailResolvedForCurrentCampaign && detailQuery.error === undefined
      ? "Cargando…"
      : hasCampaign && !detailResolvedForCurrentCampaign && detailQuery.error !== undefined
        ? "Campaña no disponible"
        : subtitleFor({ campaign: campaignForSubtitle, period });

  // Codex finding 5 (PR #385): derived from the live F2-01 capability catalog
  // (`overviewQuery.data.metrics`) instead of a static list, so it never
  // claims an already-unblocked metric (e.g. rebote_duro) is unmeasured.
  const unmeasuredMetricsNote = buildUnmeasuredMetricsNote(overviewQuery.data?.metrics);

  return (
    <section aria-label="Telemetría" className="v2-telemetry">
      <header className="v2-telemetry__header">
        <div className="v2-telemetry__heading">
          <h1>Telemetría</h1>
          <span className="v2-telemetry__subtitle">{subtitle}</span>
        </div>
        <div className="v2-telemetry__controls">
          <div className="v2-telemetry__campaign-picker">
            <CampaignSelector
              id="telemetry-campaign-selector"
              value={campaignId}
              onChange={handleSelectCampaign}
              allOptionLabel="Todas las campañas"
              compact
            />
          </div>
          {!hasCampaign && (
            <SegmentedControl
              ariaLabel="Período"
              options={[
                { value: "metrics", label: "7 días" },
                { value: "metricsAllTime", label: "Histórico" },
              ]}
              value={period}
              onChange={(value) => setPeriod(value as TelemetryPeriod)}
            />
          )}
        </div>
      </header>

      {hasCampaign && detailQuery.loading && <p role="status">Cargando campaña…</p>}

      {/* Codex review round 4 (P2, PR #385): same truthy-only `detailQuery.data`
          bug as `campaignForSubtitle` above — a retained PREVIOUS campaign's
          detail object suppressed this branch entirely once the newly-selected
          campaign's own request failed. */}
      {hasCampaign && !detailResolvedForCurrentCampaign && detailQuery.error !== undefined && (
        <CampaignNotFoundOrError error={detailQuery.error} onRetry={detailQuery.refetch} onClear={() => handleSelectCampaign("")} />
      )}

      {detailResolvedForCurrentCampaign && (
        <KpiCardsSection
          // Codex finding (PR #385 review round 3): `useQuery` retains a
          // previously-selected campaign's own `telemetryQuery.data` while
          // the newly-selected campaign's request is still in flight (or
          // fails) -- this branch accepted any truthy detail/telemetry
          // response, so the selector could name campaign B while the KPI
          // figures still belonged to campaign A, indefinitely if either
          // request then failed. A genuinely retained DATA object for the
          // WRONG campaign degrades to the loading state (never shown as
          // real figures, never as a stale error from the old campaign);
          // the current campaign's own undefined-data/error path (no retained
          // object at all) is untouched.
          metrics={telemetryStaleForCurrentCampaign ? undefined : telemetryQuery.data?.metrics}
          // Codex review round 4 (P2, PR #385): the round-3 fix forced
          // `loading: true` for ANY cross-campaign stale data with no way to
          // ever surface a settled failure — an operator whose newly-selected
          // campaign's own telemetry request failed saw an indefinite
          // "Cargando telemetría…" instead of the real error/Reintentar.
          // `refreshing` (phase: "loading" with data already present) is true
          // ONLY while the replacement request for the CURRENT key is still
          // pending; once it settles (success replaces `data` with the
          // matching campaignId, clearing staleness, or failure sets `error`
          // and stops `refreshing`), this no longer masks the real state.
          loading={telemetryQuery.loading || (telemetryStaleForCurrentCampaign && telemetryQuery.refreshing)}
          error={telemetryQuery.error}
          stale={telemetryQuery.stale}
          onRetry={telemetryQuery.refetch}
          loadingLabel="Cargando telemetría…"
          errorFallback="No se pudo cargar la telemetría de la campaña."
        />
      )}

      {!hasCampaign && (
        <KpiCardsSection
          metrics={period === "metrics" ? overviewQuery.data?.metrics : overviewQuery.data?.metricsAllTime}
          accountMetrics={overviewQuery.data?.accountMetrics}
          loading={overviewQuery.loading}
          error={overviewQuery.error}
          stale={overviewQuery.stale}
          onRetry={overviewQuery.refetch}
          loadingLabel="Cargando resumen de cuenta…"
          errorFallback="No se pudo cargar el resumen de cuenta."
          rateWindowCaveat={period === "metrics"}
        />
      )}

      <DailyChartSection query={dailyQuery} />

      <div className="v2-telemetry__cards-grid">
        <BreakdownCard groupBy={groupBy} onGroupByChange={setGroupBy} query={breakdownQuery} />
        <CampaignStateCard query={overviewQuery} />
      </div>

      {unmeasuredMetricsNote && (
        <p className="v2-telemetry__note">
          <span className="v2-telemetry__note-icon" aria-hidden="true">
            i
          </span>
          {unmeasuredMetricsNote}
        </p>
      )}
    </section>
  );
}

interface KpiCard {
  key: string;
  label: string;
  value: string;
  hint: string;
  critical?: boolean;
}

/**
 * Codex finding 4373696366 (PR #385): `AdminOverviewSqlBuilder
 * .BuildSupportedEventCountsQuery` (src/Orchestrator/Services/Admin/
 * AdminOverviewSqlBuilder.cs:39-49) filters `encolado`/`entregado`
 * INDEPENDENTLY by each event's own `se.timestamp >= @eventWindowStart` for
 * the default 7-day account scope -- a message enqueued just before the
 * cutoff but delivered just after it is counted in `entregado` while
 * excluded from `encolado`, so this ratio can read inflated (even >100%) for
 * that one scope. `metricsAllTime` (BuildSupportedEventCountsAllTimeQuery,
 * no window) and the per-campaign telemetry (AdminTelemetrySqlBuilder
 * .BuildEventCountsQuery, also no window) share one unbounded scope between
 * both counts and don't have this mismatch. No backend change: the 7-day
 * scope's hint discloses the caveat instead of presenting the ratio as exact.
 */
function buildKpiCards(metrics: OperationalMetric[], accountMetrics?: OperationalMetric[], rateWindowCaveat = false): KpiCard[] {
  const errorValue = metricValue(metrics, "error");
  const cards: KpiCard[] = [
    { key: "enviados", label: "Enviados", value: formatMetricDisplay(metrics, "encolado"), hint: "Correos puestos en cola" },
    { key: "entregados", label: "Entregados", value: formatMetricDisplay(metrics, "entregado"), hint: "Aceptados por el servidor" },
    {
      key: "errores",
      label: "Errores",
      value: formatMetricDisplay(metrics, "error"),
      hint: "No se pudieron entregar",
      critical: errorValue !== null && errorValue > 0,
    },
    {
      key: "tasa",
      label: "Tasa de entrega",
      value: formatDeliveryRateFromMetrics(metrics),
      hint: rateWindowCaveat ? "Entregados ÷ enviados (ventanas de 7 días independientes; referencial)" : "Entregados ÷ enviados",
    },
  ];
  if (accountMetrics) {
    cards.push({
      key: "suprimidos",
      label: "Contactos suprimidos",
      value: formatMetricDisplay(accountMetrics, "suprimidos"),
      hint: "Estado actual de la cuenta",
    });
  }
  return cards;
}

/** §6 "Tarjetas de cifras" — shared by the account-wide and per-campaign scopes; only the data source and the presence of `accountMetrics` differ. */
function KpiCardsSection({
  metrics,
  accountMetrics,
  loading,
  error,
  stale,
  onRetry,
  loadingLabel,
  errorFallback,
  rateWindowCaveat = false,
}: {
  metrics: OperationalMetric[] | undefined;
  accountMetrics?: OperationalMetric[];
  loading: boolean;
  error: unknown;
  stale: boolean;
  onRetry: () => void;
  loadingLabel: string;
  errorFallback: string;
  /** Codex finding 4373696366 (PR #385): only the 7-day account-wide scope's independently-windowed counts need the "Tasa de entrega" referencial caveat. */
  rateWindowCaveat?: boolean;
}) {
  if (loading) return <p role="status">{loadingLabel}</p>;
  if (!metrics && error !== undefined) {
    return <FeedbackPanel variant="error" message={describeError(error, errorFallback)} onRetry={onRetry} />;
  }
  if (!metrics) return null;

  const cards = buildKpiCards(metrics, accountMetrics, rateWindowCaveat);

  return (
    <>
      {stale && <FeedbackPanel variant="stale" message="Datos anteriores de telemetría." onRetry={onRetry} />}
      <div className="v2-telemetry__kpi-grid">
        {cards.map((card) => (
          <div key={card.key} className={card.critical ? "v2-telemetry__kpi-card v2-telemetry__kpi-card--critical" : "v2-telemetry__kpi-card"}>
            <span className="v2-telemetry__kpi-label">{card.label}</span>
            <span className="v2-telemetry__kpi-value v2-u-tabular-nums">{card.value}</span>
            <span className="v2-telemetry__kpi-hint">{card.hint}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/** §6 "Envíos por día · últimos 30 días · toda la cuenta". */
function DailyChartSection({ query }: { query: UseQueryResult<AdminTelemetryDailySeries> }) {
  return (
    <section aria-label="Envíos por día" className="v2-telemetry__chart-card">
      <div className="v2-telemetry__chart-header">
        <h2>Envíos por día · últimos 30 días · toda la cuenta</h2>
        <ul className="v2-telemetry__chart-legend">
          <li>
            <span className="v2-telemetry__legend-swatch v2-telemetry__legend-swatch--delivered" aria-hidden="true" />
            Entregados
          </li>
          <li>
            <span className="v2-telemetry__legend-swatch v2-telemetry__legend-swatch--error" aria-hidden="true" />
            Errores
          </li>
        </ul>
      </div>

      {query.loading && <p role="status">Cargando envíos por día…</p>}

      {!query.data && query.error !== undefined && (
        <FeedbackPanel variant="error" message={describeError(query.error, "No se pudo cargar los envíos por día.")} onRetry={query.refetch} />
      )}

      {query.data && (
        <>
          {query.stale && <FeedbackPanel variant="stale" message="Datos anteriores de envíos por día." onRetry={query.refetch} />}
          <DailyChart series={query.data} />
        </>
      )}
    </section>
  );
}

function DailyChart({ series }: { series: AdminTelemetryDailySeries }) {
  const { bars, tickLabels } = buildDailyChartData(series);

  return (
    <>
      <div
        className="v2-telemetry__chart-bars"
        role="img"
        aria-label={`Envíos por día de los últimos ${series.days} días, toda la cuenta — ver la tabla siguiente para el detalle`}
      >
        {bars.map((bar) =>
          bar.isZero ? (
            <div key={bar.fecha} className="v2-telemetry__chart-bar" title={bar.title}>
              <span className="v2-telemetry__chart-bar-zero" />
            </div>
          ) : (
            <div key={bar.fecha} className="v2-telemetry__chart-bar" title={bar.title}>
              <span className="v2-telemetry__chart-bar-error" style={{ height: `${bar.errorPercent}%` }} />
              <span className="v2-telemetry__chart-bar-delivered" style={{ height: `${bar.deliveredPercent}%` }} />
            </div>
          ),
        )}
      </div>
      <div className="v2-telemetry__chart-ticks">
        {tickLabels.map((label, index) => (
          // Tick labels are not guaranteed unique across a short/degenerate
          // window — suffixing with the (deduped, stable) tick index keeps
          // every key unique without relying on index alone.
          <span key={`${label}-${index}`}>{label}</span>
        ))}
      </div>
      {/* Codex finding 6 (PR #385): a bar's exact figures previously lived
          ONLY behind the `title` hover tooltip (mouse-only) and this
          always-present-but-visually-hidden table (screen readers only) --
          a sighted keyboard/touch user with no mouse had no path to them.
          A native, VISIBLE `<details>/<summary>` disclosure (this
          codebase's established toggle pattern) gives every input method
          a way to reach the same data. */}
      {/* Round-2 client decision: the visible "Ver datos en tabla" disclosure
          is removed (the prototype has none); the per-day figures stay
          available to assistive tech as a visually hidden table. */}
      <table className="v2-u-visually-hidden">
        <caption>Envíos por día (vista en tabla de la gráfica anterior)</caption>
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Entregados</th>
            <th>Errores</th>
          </tr>
        </thead>
        <tbody>
          {bars.map((bar) => (
            <tr key={bar.fecha}>
              <td>{bar.fecha}</td>
              <td>{formatCount(bar.entregado)}</td>
              <td>{formatCount(bar.error)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

const BREAKDOWN_PAGE_SIZE = 5;

/** One page of the breakdown list; the page is clamped when the data shrinks under it. */
function pageOfGroups<T>(groups: readonly T[], requestedPage: number): { items: T[]; page: number } {
  const totalPages = Math.max(1, Math.ceil(groups.length / BREAKDOWN_PAGE_SIZE));
  const page = Math.min(Math.max(1, requestedPage), totalPages);
  return { items: groups.slice((page - 1) * BREAKDOWN_PAGE_SIZE, page * BREAKDOWN_PAGE_SIZE), page };
}

/** §6 "Rendimiento por plantilla | show". */
function BreakdownCard({
  groupBy,
  onGroupByChange,
  query,
}: {
  groupBy: AdminTelemetryGroupBy;
  onGroupByChange: (groupBy: AdminTelemetryGroupBy) => void;
  query: UseQueryResult<AdminTelemetryBreakdown>;
}) {
  const groupLabelLower = groupBy === "show" ? "show" : "plantilla";
  // Panel feedback 2026-10-30: the list shows BREAKDOWN_PAGE_SIZE rows per
  // page. The page is tagged with the groupBy it belongs to, so toggling
  // Plantilla/Show starts at page 1 again without an extra reset effect.
  const [pageState, setPageState] = useState<{ groupBy: AdminTelemetryGroupBy; page: number }>({ groupBy, page: 1 });
  // Codex finding 1 (PR #385): `useQuery` keeps the PREVIOUS key's cached
  // data visible (and `loading: false`) while a newly-keyed fetch for the
  // just-selected groupBy is still in flight (state/useQuery.ts) -- without
  // this check, the stale response's groups briefly rendered under the NEW
  // groupBy's labels. The response itself carries its own `groupBy`
  // (`AdminTelemetryBreakdown.groupBy`); treating a mismatch as still-loading
  // ties the label to the data it actually describes instead of inventing a
  // second page-tagging mechanism.
  //
  // Codex finding 4373696358 (PR #385): the FIRST version of this check
  // ("mismatch alone means loading") kept `isLoadingForSelection` true
  // FOREVER once the replacement request for the new groupBy settled with an
  // error -- `query.loading` was already `false` by then, but the old key's
  // data was still cached (and still mismatched), so the spinner never let
  // go.
  //
  // Native advisory (TelemetryScreen.tsx:~443): a SECOND version of this
  // check additionally required `query.refreshing` to be `true` for the
  // mismatch case -- but on the VERY FIRST render right after a groupBy
  // toggle, the underlying hook's key-change effect may not have run yet, so
  // `loading`/`refreshing`/`error` can all still read `false` while `data`
  // holds the OLD key's mismatched response for one render. Any mismatch is
  // treated as loading UNLESS an error for the current key is already
  // present -- that still lets a genuinely failed replacement request (Codex
  // finding 4373696358) surface its error instead of spinning forever.
  //
  // Regression (PR cd26976): that fix dropped the `query.data !== undefined`
  // guard entirely, so a SETTLED, error-free response that never actually
  // gets re-tagged for the new key (the replacement request resolves without
  // changing `data`, or never re-fires at all) spun "Cargando rendimiento…"
  // forever -- `query.loading`/`query.refreshing` both go back to `false`
  // once the request settles, but the mismatch (and the `undefined` error)
  // never clear on their own. A settled mismatch now triggers exactly ONE
  // `refetch()` for the current groupBy (giving a genuinely in-flight-but-
  // not-yet-reflected request, or a one-off bad response, a real chance to
  // resolve correctly) and, once that one retry has already been attempted,
  // stops treating the still-mismatched result as loading -- surfacing the
  // same error/empty fallback as a failed request instead of an infinite
  // spinner with no further corrective action.
  const resultMatchesSelection = query.data?.groupBy === groupBy;
  const requestedPage = pageState.groupBy === groupBy ? pageState.page : 1;
  const settledMismatchWithoutError = !resultMatchesSelection && !query.loading && !query.refreshing && query.error === undefined;
  const retryAttemptedForRef = useRef<AdminTelemetryGroupBy | null>(null);
  const retryExhausted = retryAttemptedForRef.current === groupBy;

  useEffect(() => {
    if (settledMismatchWithoutError) {
      if (retryAttemptedForRef.current !== groupBy) {
        retryAttemptedForRef.current = groupBy;
        query.refetch();
      }
    } else if (resultMatchesSelection) {
      // A fresh match for this groupBy clears the retry memory so toggling
      // away and back later gets its own, independent retry attempt.
      retryAttemptedForRef.current = null;
    }
  }, [settledMismatchWithoutError, resultMatchesSelection, groupBy, query]);

  // Scoped to the mismatch case (not a blanket `query.refreshing`) -- a
  // background refresh of data that ALREADY matches the current groupBy must
  // keep showing that (still-correct) data, not hide it behind a spinner.
  const mismatchInFlight = !resultMatchesSelection && (query.loading || query.refreshing);
  const isLoadingForSelection = mismatchInFlight || (settledMismatchWithoutError && !retryExhausted);
  const stuckMismatch = settledMismatchWithoutError && retryExhausted;

  return (
    <section aria-label={`Rendimiento por ${groupLabelLower}`} className="v2-telemetry__card">
      <div className="v2-telemetry__card-header">
        <div className="v2-telemetry__card-heading">
          <h2>Rendimiento por {groupLabelLower}</h2>
          {/* Codex finding 2 (PR #385): AdminTelemetryBreakdownModels.cs
              documents this query as "Historico completo (sin ventana de
              tiempo)" -- it never filters by campaign or period, unlike the
              page subtitle above it (which can show a selected campaign's
              name/estado, or the "7 días" period). Disclosing the card's
              real, fixed scope here keeps it from being misread as scoped
              to whatever the page subtitle currently shows. */}
          <span className="v2-telemetry__card-scope">Toda la cuenta · desde el inicio</span>
        </div>
        <SegmentedControl
          ariaLabel="Agrupar por"
          options={[
            { value: "template", label: "Plantilla" },
            { value: "show", label: "Show" },
          ]}
          value={groupBy}
          onChange={(value) => onGroupByChange(value as AdminTelemetryGroupBy)}
        />
      </div>

      {isLoadingForSelection && <p role="status">Cargando rendimiento…</p>}

      {/* `!resultMatchesSelection` (not `!query.data`) so a failed replacement
          request's error surfaces even while the OLD key's mismatched data is
          still cached (Codex finding 4373696358) -- `resultMatchesSelection`
          is only ever true when `query.data` both exists AND matches the
          current `groupBy`. `stuckMismatch` surfaces the same fallback for a
          settled, error-free mismatch whose one retry attempt has already
          been used up (regression fix above) -- never an infinite spinner. */}
      {!isLoadingForSelection && !resultMatchesSelection && (query.error !== undefined || stuckMismatch) && (
        <FeedbackPanel
          variant="error"
          message={describeError(query.error, "No se pudo cargar el rendimiento.")}
          onRetry={query.refetch}
        />
      )}

      {!isLoadingForSelection && query.data && resultMatchesSelection && (
        <>
          {query.stale && <FeedbackPanel variant="stale" message="Datos anteriores de rendimiento." onRetry={query.refetch} />}
          {query.data.groups.length === 0 ? (
            <EmptyState
              title="Sin actividad medida todavía"
              description="Ningún show o plantilla tiene actividad medida todavía (solo se listan los que sí la tienen)."
            />
          ) : (
            <>
            <ul className="v2-telemetry__breakdown-list">
              {pageOfGroups(query.data.groups, requestedPage).items.map((group) => {
                const name = groupLabelFor(group.nombre, groupBy);
                const deliveredValue = metricValue(group.metrics, "entregado");
                const errorCount = metricValue(group.metrics, "error") ?? 0;
                const total = metricValue(group.metrics, "encolado") ?? 0;
                // Codex finding (PR #385 review round 3): `encolado`,
                // `entregado`, and `error` are recorded as INDEPENDENT
                // events (CampaignDispatchService.cs:379-389) — an error
                // can occur for a recipient that never reached `encolado`
                // at all, so it is not guaranteed to fit inside this bar's
                // own enqueued-only denominator. Feeding it in anyway when
                // it doesn't fit (delivered + error > total) makes
                // DeliveryProgressBar treat the inputs as inconsistent and
                // normalize the announced delivered count down to 0 — a
                // fully red bar even though the real delivered figure is
                // correct. Only pass the error count into the bar when it
                // is cohort-aligned (fits the enqueued total together with
                // delivered); the true figures still surface via the meta
                // line below regardless.
                const barErrorCount = (deliveredValue ?? 0) + errorCount <= total ? errorCount : 0;
                return (
                  <li key={group.id ?? name} className="v2-telemetry__breakdown-row">
                    <div className="v2-telemetry__breakdown-row-head">
                      <span className="v2-telemetry__breakdown-name">{name}</span>
                      <span className="v2-u-tabular-nums">{formatDeliveryRateFromMetrics(group.metrics)} entregado</span>
                    </div>
                    <DeliveryProgressBar
                      delivered={deliveredValue ?? 0}
                      errorCount={barErrorCount}
                      total={total}
                      height={6}
                      // Codex finding 4373696364 (PR #385): when `entregado` is
                      // unavailable, `delivered` above is only a fed-in `0` for
                      // the bar's own geometry -- announcing "0 de N
                      // entregados" to assistive tech would read as a
                      // confirmed measurement, not a missing one (same fix
                      // CampaignDetailScreen already applies).
                      deliveredAvailable={deliveredValue !== null}
                    />
                    <span className="v2-telemetry__breakdown-meta v2-u-tabular-nums">{formatBreakdownMeta(group.metrics)}</span>
                  </li>
                );
              })}
            </ul>
            {query.data.groups.length > BREAKDOWN_PAGE_SIZE && (
              <Pagination
                variant="range"
                page={pageOfGroups(query.data.groups, requestedPage).page}
                size={BREAKDOWN_PAGE_SIZE}
                total={query.data.groups.length}
                onPageChange={(page) => setPageState({ groupBy, page })}
                onSizeChange={() => undefined}
              />
            )}
            </>
          )}
        </>
      )}
    </section>
  );
}

/** §6 "Campañas por estado". */
function CampaignStateCard({ query }: { query: UseQueryResult<AdminOverview> }) {
  return (
    <section aria-label="Campañas por estado" className="v2-telemetry__card">
      <div className="v2-telemetry__card-heading">
        <h2>Campañas por estado</h2>
        {/* Codex round 6 (PR #385): always account-wide, current-state data
            (overview.campaignStates), regardless of the selected campaign or
            period shown in the page subtitle. */}
        <span className="v2-telemetry__card-scope">Toda la cuenta · estado actual</span>
      </div>

      {query.loading && <p role="status">Cargando campañas por estado…</p>}

      {!query.data && query.error !== undefined && (
        <FeedbackPanel
          variant="error"
          message={describeError(query.error, "No se pudo cargar el estado de las campañas.")}
          onRetry={query.refetch}
        />
      )}

      {query.data && (
        <>
          {query.stale && (
            <FeedbackPanel variant="stale" message="Datos anteriores de campañas por estado." onRetry={query.refetch} />
          )}
          <CampaignStateDistributionView states={query.data.campaignStates} />
        </>
      )}
    </section>
  );
}

function CampaignStateDistributionView({ states }: { states: CampaignStateCount[] }) {
  const { bar, legend } = buildCampaignStateDistribution(states);

  return (
    <>
      <div
        className="v2-telemetry__state-bar"
        role="img"
        aria-label={legend.map((entry) => `${entry.label}: ${entry.count}`).join(", ")}
      >
        {bar.map((segment) => (
          <span
            key={segment.state}
            title={segment.label}
            className={`v2-telemetry__state-bar-segment v2-telemetry__state-bar-segment--${segment.tone}`}
            style={{ width: `${segment.percent}%` }}
          />
        ))}
      </div>
      <ul className="v2-telemetry__state-legend">
        {legend.map((entry) => (
          <li
            key={entry.state}
            className={
              entry.isZero
                ? "v2-telemetry__state-legend-item v2-telemetry__state-legend-item--zero"
                : "v2-telemetry__state-legend-item"
            }
          >
            <span className={`v2-telemetry__state-legend-dot v2-telemetry__state-legend-dot--${entry.tone}`} aria-hidden="true" />
            <span className="v2-telemetry__state-legend-label">{entry.label}</span>
            <span className="v2-u-tabular-nums">{formatCount(entry.count)}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The identical local not-found state `CampaignDetailScreen` renders for
 * `ApiError.kind === "notFound"` (task 10.7's "local 404 recovery UI for
 * nonexistent/foreign campaigns") — plus an explicit recovery action to
 * clear the selection and return to the selector.
 */
function CampaignNotFoundOrError({
  error,
  onRetry,
  onClear,
}: {
  error: unknown;
  onRetry: () => void;
  onClear: () => void;
}) {
  if (error instanceof ApiError && error.kind === "notFound") {
    return (
      <section aria-label="Campaña no encontrada">
        <h2>Campaña no encontrada</h2>
        <p>La campaña solicitada no existe o no está disponible para esta cuenta.</p>
        <button type="button" className="v2-u-target-min" onClick={onClear}>
          Elegir otra campaña
        </button>
      </section>
    );
  }

  return <FeedbackPanel variant="error" message={describeError(error, "No se pudo cargar la campaña.")} onRetry={onRetry} />;
}
